// Pure statistics for the Summary page. Extracted from SummaryPage.js so the
// numbers can be unit-tested. Unless a comment says "fixed", every function here
// reproduces the original page's arithmetic exactly (see summaryStats.test.js).
import { classifyTradeOutcomeByThreshold, DEFAULT_BE_THRESHOLD, getBalanceBeforeFirstMatchingTrade, sanitizeBeThreshold } from '../statsUtils';
import { cleanCreatedTimestamp, getCreatedTimeMsFromCreated } from './dateUtils';
import { isTapeReading } from './tradeHelpers';
import { getInsightDate } from './performanceInsights';
import { findBestGroup, getDayOfWeek } from '../components/analytics/visualizationData';

export const NEW_ACCOUNT_PERCENT = 999999;

export const TIME_PERIOD_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '1month', label: 'Last 30 days' },
  { value: '3months', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'custom', label: 'Custom range' },
];

/** Sums numbers/numeric strings as cents and returns a float. */
export function sumPrecise(arr) {
  return arr.reduce((sum, v) => sum + Math.round(Number(v) * 100), 0) / 100;
}

/** Trade date from year/month/day, falling back to the `created` timestamp. */
export function getEntryDate(entry) {
  if (entry.year && entry.month && entry.day) {
    const year = parseInt(entry.year, 10);
    const month = parseInt(entry.month, 10) - 1;
    const day = parseInt(entry.day, 10);
    return new Date(year, month, day);
  } else if (entry.created) {
    const lastDashIndex = entry.created.lastIndexOf('-');
    const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
    return new Date(cleanTimestamp);
  }
  return null;
}

/** An actual trade: not a deposit/payout/tape reading/reset-excluded, with a numeric pnl. */
export function isTradeEntry(e) {
  if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) {
    return false;
  }
  const pnlValue = e.pnl;
  if (pnlValue === undefined || pnlValue === null || pnlValue === '') {
    return false;
  }
  return !isNaN(Number(pnlValue));
}

/** Custom range falls back to all time until both dates are set. */
export function resolveEffectivePeriod(selectedTimePeriod, customStartDate = '', customEndDate = '') {
  const incompleteCustom = selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate);
  return {
    period: incompleteCustom ? 'all' : selectedTimePeriod,
    startDate: incompleteCustom ? '' : customStartDate,
    endDate: incompleteCustom ? '' : customEndDate,
  };
}

/** `{ start, end, active }` for a period. `now` is injectable for tests. */
export function getTimeRange(selectedTimePeriod, customStartDate = '', customEndDate = '', now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  if (selectedTimePeriod === '1month' || selectedTimePeriod === '3months') {
    const start = new Date(today);
    start.setDate(start.getDate() - (selectedTimePeriod === '1month' ? 30 : 90));
    start.setHours(0, 0, 0, 0);
    return { start, end: endOfToday, active: true };
  }

  if (selectedTimePeriod === 'ytd') {
    const start = new Date(now.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0);
    return { start, end: endOfToday, active: true };
  }

  if (selectedTimePeriod === 'custom' && customStartDate && customEndDate) {
    // Parse YYYY-MM-DD as local dates (new Date('YYYY-MM-DD') would be UTC).
    const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    start.setHours(0, 0, 0, 0);
    const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
    const end = new Date(endYear, endMonth - 1, endDay);
    end.setHours(23, 59, 59, 999);
    return { start, end, active: true };
  }

  return { start: null, end: null, active: false };
}

export function filterEntriesByRange(entries, range) {
  if (!range.active) return entries;
  return entries.filter((e) => {
    const entryDate = getEntryDate(e);
    if (!entryDate || isNaN(entryDate.getTime())) return false;
    const normalizedEntryDate = new Date(entryDate);
    normalizedEntryDate.setHours(0, 0, 0, 0);
    return normalizedEntryDate >= range.start && normalizedEntryDate <= range.end;
  });
}

/** ISO week number. */
export function getWeekNumber(d) {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  week1.setHours(0, 0, 0, 0);
  week1.setDate(week1.getDate() + 3 - ((week1.getDay() + 6) % 7));
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

const EMPTY_STATS = {
  totalPnl: 0,
  avgPnl: 0,
  avgDuration: 0,
  winRate: 0,
  avgWeekly: 0,
  avgMonthly: 0,
  avgRr: 0,
  wins: 0,
  losses: 0,
  breakeven: 0,
  totalTrades: 0,
  avgWinningTrade: 0,
  avgLosingTrade: 0,
  byMonth: {},
  byWeek: {},
};

/** Headline trade statistics for the selected period. */
export function getStats(entries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '', beThreshold = DEFAULT_BE_THRESHOLD, now = new Date()) {
  if (!entries.length) return { ...EMPTY_STATS, byMonth: {}, byWeek: {} };

  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate, now);
  const resolvedBeThreshold = sanitizeBeThreshold(beThreshold);
  const tradeEntries = filterEntriesByRange(entries, range).filter(isTradeEntry);
  const totalPnl = sumPrecise(tradeEntries.map((e) => e.pnl));
  const avgPnl = tradeEntries.length ? totalPnl / tradeEntries.length : 0;
  const avgDuration = tradeEntries.length
    ? tradeEntries.reduce((sum, e) => sum + (parseFloat(e.duration) || 0), 0) / tradeEntries.length
    : 0;

  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  const winningTrades = [];
  const losingTrades = [];
  tradeEntries.forEach((entry) => {
    const outcome = classifyTradeOutcomeByThreshold(entry.pnl, resolvedBeThreshold);
    if (outcome === 'win') {
      wins += 1;
      winningTrades.push(entry);
    } else if (outcome === 'loss') {
      losses += 1;
      losingTrades.push(entry);
    } else {
      breakeven += 1;
    }
  });
  const decisiveTrades = wins + losses;
  const winRate = decisiveTrades ? (wins / decisiveTrades) * 100 : 0;
  const avgRr = tradeEntries.length ? sumPrecise(tradeEntries.map((e) => e.rr)) / tradeEntries.length : 0;
  const avgWinningTrade = winningTrades.length ? sumPrecise(winningTrades.map((e) => e.pnl)) / winningTrades.length : 0;
  const avgLosingTrade = losingTrades.length ? sumPrecise(losingTrades.map((e) => e.pnl)) / losingTrades.length : 0;

  const byWeek = {};
  const byMonth = {};
  tradeEntries.forEach((e) => {
    let d;
    if (e.year && e.month && e.day) {
      d = new Date(parseInt(e.year, 10), parseInt(e.month, 10) - 1, parseInt(e.day, 10));
    } else {
      d = new Date(e.created);
    }
    const week = `${d.getFullYear()}-W${getWeekNumber(d)}`;
    const month = `${d.getFullYear()}-${d.getMonth() + 1}`;
    const pnlValue = Number(e.pnl) || 0;
    byWeek[week] = Math.round(((byWeek[week] || 0) + pnlValue) * 100) / 100;
    byMonth[month] = Math.round(((byMonth[month] || 0) + pnlValue) * 100) / 100;
  });
  const weekValues = Object.values(byWeek);
  const monthValues = Object.values(byMonth);
  const avgWeekly = weekValues.length ? weekValues.reduce((a, b) => a + b, 0) / weekValues.length : 0;
  const avgMonthly = monthValues.length ? monthValues.reduce((a, b) => a + b, 0) / monthValues.length : 0;

  return {
    totalPnl,
    avgPnl,
    avgDuration,
    winRate,
    avgWeekly,
    avgMonthly,
    avgRr,
    wins,
    losses,
    breakeven,
    totalTrades: tradeEntries.length,
    avgWinningTrade,
    avgLosingTrade,
    byMonth,
    byWeek,
  };
}

function getEquityDateString(entry) {
  if (entry.year && entry.month && entry.day) {
    const year = String(entry.year).padStart(4, '0');
    const month = String(entry.month).padStart(2, '0');
    const day = String(entry.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (entry.created) {
    const lastDashIndex = entry.created.lastIndexOf('-');
    if (lastDashIndex > 10) {
      const date = new Date(entry.created.substring(0, lastDashIndex));
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day}`;
    }
  }
  return null;
}

/** Ledger sort key used by the equity curve: `created` first, then the trade date. */
function getLedgerTimestamp(entry) {
  if (entry.created) return getCreatedTimeMsFromCreated(entry.created);
  if (entry.year && entry.month && entry.day) {
    return new Date(parseInt(entry.year, 10), parseInt(entry.month, 10) - 1, parseInt(entry.day, 10)).getTime();
  }
  return 0;
}

/** Applies one ledger entry to a running balance (balance adjustments set it outright). */
function applyLedgerEntry(balance, e) {
  if (e.isBalanceAdjustment) {
    const targetBalance = Number(e.adjustmentTarget ?? e.accountBalance);
    return Number.isFinite(targetBalance) ? targetBalance : balance + (Number(e.pnl) || 0);
  }
  if (e.isDeposit || e.isPayout) return balance + (Number(e.pnl) || 0); // payouts are stored negative
  if (!isTapeReading(e) && !e.isResetExcluded) return balance + (Number(e.pnl) || 0);
  return balance;
}

/**
 * Equity curve points for the period. Filtered ranges start from the carry-in
 * balance (everything before the range start), so the curve shows the real balance.
 */
export function getEquityCurve(allEntries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '', now = new Date()) {
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate, now);
  const filteredEntries = filterEntriesByRange(allEntries, range);

  const tradesByDate = {};
  filteredEntries.forEach((entry) => {
    if (!isTapeReading(entry) && !entry.isResetExcluded && !entry.isDeposit && !entry.isPayout) {
      const dateStr = getEquityDateString(entry);
      if (dateStr) tradesByDate[dateStr] = (tradesByDate[dateStr] || 0) + 1;
    }
  });

  const sorted = [...filteredEntries].sort((a, b) => getLedgerTimestamp(a) - getLedgerTimestamp(b));

  let carryInBalance = 0;
  if (range.active && range.start) {
    const entriesBeforeRange = allEntries.filter((e) => {
      const entryDate = getEntryDate(e);
      if (!entryDate || isNaN(entryDate.getTime())) return false;
      const normalizedDate = new Date(entryDate);
      normalizedDate.setHours(0, 0, 0, 0);
      return normalizedDate < range.start;
    });
    [...entriesBeforeRange]
      .sort((a, b) => getLedgerTimestamp(a) - getLedgerTimestamp(b))
      .forEach((e) => { carryInBalance = applyLedgerEntry(carryInBalance, e); });
    carryInBalance = Math.round(carryInBalance * 100) / 100;
  }

  const curve = [];
  const points = [];
  let balance = carryInBalance;
  const startLabel = range.active ? `Start (${range.start.toLocaleDateString()})` : 'Start';
  curve.push(balance);
  points.push({ x: 0, y: balance, date: startLabel, tradeCount: 0, accountBalance: balance });

  sorted.forEach((e) => {
    let entryType = '';
    let displayPnl = 0;
    let shouldAddToCurve = false;

    if (e.isBalanceAdjustment) {
      balance = applyLedgerEntry(balance, e);
      entryType = 'Balance Adjustment';
      displayPnl = Number(e.pnl) || 0;
      shouldAddToCurve = true;
    } else if (e.isDeposit) {
      const depositAmount = Number(e.pnl) || 0;
      balance += depositAmount;
      entryType = 'Deposit';
      displayPnl = depositAmount;
      shouldAddToCurve = true;
    } else if (e.isPayout) {
      const payoutAmount = Number(e.pnl) || 0;
      balance += payoutAmount;
      entryType = 'Payout';
      displayPnl = Math.abs(payoutAmount);
      shouldAddToCurve = true;
    } else if (!isTapeReading(e) && !e.isResetExcluded) {
      const tradePnl = Number(e.pnl) || 0;
      balance += tradePnl;
      entryType = 'Trade';
      displayPnl = tradePnl;
      shouldAddToCurve = true;
    }

    balance = Math.round(balance * 100) / 100;

    if (shouldAddToCurve) {
      curve.push(balance);
      const entryDate = e.created ? new Date(cleanCreatedTimestamp(e.created)) : new Date();
      const dateStr = getEquityDateString(e);
      points.push({
        x: points.length,
        y: balance,
        date: entryDate.toLocaleDateString(),
        time: entryDate.toLocaleTimeString(),
        type: entryType,
        pnl: displayPnl,
        ticker: e.tickerTraded || '',
        title: e.title || '',
        accountBalance: balance,
        tradeCount: dateStr ? (tradesByDate[dateStr] || 0) : 0,
      });
    }
  });

  return { curve, points };
}

/**
 * Chronological order for trades: trade date (year/month/day, else created),
 * then `created` time, then id. Fixed: streaks used to depend on Firestore
 * document-id order for trades on the same day.
 */
export function compareTradesChronologically(a, b) {
  const dateA = getEntryDate(a);
  const dateB = getEntryDate(b);
  const dayA = dateA && !isNaN(dateA.getTime()) ? new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate()).getTime() : 0;
  const dayB = dateB && !isNaN(dateB.getTime()) ? new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate()).getTime() : 0;
  if (dayA !== dayB) return dayA - dayB;
  const createdDiff = getCreatedTimeMsFromCreated(a.created) - getCreatedTimeMsFromCreated(b.created);
  if (createdDiff !== 0) return createdDiff;
  return String(a.id || '').localeCompare(String(b.id || ''));
}

/** Current and longest win/loss streaks (by raw sign of P&L). */
export function getStreaks(entries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '', now = new Date()) {
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate, now);
  const tradingEntries = filterEntriesByRange(entries, range)
    .filter(isTradeEntry)
    .sort(compareTradesChronologically);

  let greenStreak = 0;
  let lossStreak = 0;
  let maxGreen = 0;
  let maxLoss = 0;
  tradingEntries.forEach((e) => {
    const pnl = Number(e.pnl);
    if (pnl > 0) {
      greenStreak += 1;
      lossStreak = 0;
      maxGreen = Math.max(maxGreen, greenStreak);
    } else if (pnl < 0) {
      greenStreak = 0;
      lossStreak += 1;
      maxLoss = Math.max(maxLoss, lossStreak);
    } else {
      greenStreak = 0;
      lossStreak = 0;
    }
  });

  return { greenStreak, lossStreak, maxGreen, maxLoss };
}

/**
 * Best trade (highest P&L) and worst losing trade (lowest P&L below zero).
 * Fixed: P&L is compared as a number; stored strings used to compare
 * alphabetically ("90" > "500"). Ties keep the original rule (later trade wins).
 */
export function getBestWorstTrades(tradeEntries) {
  const bestTrade = tradeEntries.length
    ? tradeEntries.reduce((a, b) => (Number(a.pnl) > Number(b.pnl) ? a : b))
    : null;
  const losses = tradeEntries.filter((trade) => Number(trade.pnl) < 0);
  const worstTrade = losses.length
    ? losses.reduce((a, b) => (Number(a.pnl) < Number(b.pnl) ? a : b))
    : null;
  return { bestTrade, worstTrade };
}

function percentOf(pnl, capital, hasTrades = true) {
  if (!hasTrades) return 0;
  if (capital > 0) return (pnl / capital) * 100;
  return pnl > 0 ? NEW_ACCOUNT_PERCENT : 0;
}

/**
 * Today / this week (Monday start) / this month / overall trading P&L and % of
 * deposited capital, plus the custom-range card. Matches the original page.
 */
export function getPercentageChanges(entries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '', now = new Date()) {
  if (!entries.length) return null;

  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate, now);
  const sortedEntries = [...filterEntriesByRange(entries, range)].sort(
    (a, b) => getCreatedTimeMsFromCreated(a.created) - getCreatedTimeMsFromCreated(b.created)
  );

  // All time uses every entry; other periods use the filtered entries.
  const scoped = selectedTimePeriod === 'all' ? entries : sortedEntries;
  const totalDeposits = sumPrecise(scoped.filter((e) => e.isDeposit).map((e) => Number(e.pnl) || 0));
  const totalPayouts = sumPrecise(scoped.filter((e) => e.isPayout).map((e) => Number(e.pnl) || 0));

  let originalCapital = totalDeposits;
  if (totalDeposits === 0 && totalPayouts === 0) {
    originalCapital = 0;
  } else if (totalDeposits === 0 && totalPayouts !== 0) {
    originalCapital = Math.abs(totalPayouts);
  }

  // Funded by a balance adjustment (no deposit rows)? Use the ledger balance before the first trade.
  if (originalCapital <= 0) {
    const ledgerCapital = getBalanceBeforeFirstMatchingTrade(scoped, () => true);
    if (ledgerCapital !== null && ledgerCapital > 0) originalCapital = ledgerCapital;
  }

  const totalTradingPnl = sumPrecise(scoped.filter(isTradeEntry).map((e) => Number(e.pnl) || 0));
  const overallPercentage = percentOf(totalTradingPnl, originalCapital);

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();

  const getTradesInPeriod = (startDate, endDate) => sortedEntries.filter((e) => {
    if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) return false;
    const entryDate = getEntryDate(e);
    return entryDate >= startDate && entryDate <= endDate;
  });

  const startOfToday = new Date(currentYear, currentMonth - 1, currentDay, 0, 0, 0);
  const endOfToday = new Date(currentYear, currentMonth - 1, currentDay, 23, 59, 59);
  const startOfWeek = new Date(now);
  const dayOfWeek = now.getDay();
  startOfWeek.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  startOfWeek.setHours(0, 0, 0, 0);
  const startOfMonth = new Date(currentYear, currentMonth - 1, 1);

  const todayTrades = getTradesInPeriod(startOfToday, endOfToday);
  const weekTrades = getTradesInPeriod(startOfWeek, now);
  const monthTrades = getTradesInPeriod(startOfMonth, now);

  const dayPnl = sumPrecise(todayTrades.map((e) => Number(e.pnl) || 0));
  const weekPnl = sumPrecise(weekTrades.map((e) => Number(e.pnl) || 0));
  const monthPnl = sumPrecise(monthTrades.map((e) => Number(e.pnl) || 0));

  let customPeriod = null;
  if (selectedTimePeriod === 'custom' && customStartDate && customEndDate) {
    customPeriod = getCustomPeriodChange(entries, customStartDate, customEndDate);
  }

  return {
    day: { pnl: dayPnl, percentage: percentOf(dayPnl, originalCapital, todayTrades.length > 0), hasTrades: todayTrades.length > 0 },
    week: { pnl: weekPnl, percentage: percentOf(weekPnl, originalCapital, weekTrades.length > 0), hasTrades: weekTrades.length > 0 },
    month: { pnl: monthPnl, percentage: percentOf(monthPnl, originalCapital, monthTrades.length > 0), hasTrades: monthTrades.length > 0 },
    overall: { pnl: totalTradingPnl, percentage: overallPercentage, startingCapital: originalCapital },
    custom: customPeriod,
  };
}

function getCustomPeriodChange(entries, customStartDate, customEndDate) {
  const { start: customStart, end: customEnd } = getTimeRange('custom', customStartDate, customEndDate);
  const inRange = (entry) => {
    const entryDate = getEntryDate(entry);
    if (!entryDate || isNaN(entryDate.getTime())) return false;
    const normalizedDate = new Date(entryDate);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate >= customStart && normalizedDate <= customEnd;
  };

  const customTrades = entries.filter((e) => {
    if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) return false;
    return inRange(e);
  });
  const customPnl = sumPrecise(customTrades.map((e) => Number(e.pnl) || 0));

  // Capital base: balance just before the first trade in the range.
  const allEntriesSorted = [...entries].sort(
    (a, b) => getCreatedTimeMsFromCreated(a.created) - getCreatedTimeMsFromCreated(b.created)
  );
  let runningCapital = 0;
  let capitalBeforeFirstCustomTrade = null;
  allEntriesSorted.forEach((entry) => {
    if (capitalBeforeFirstCustomTrade === null && isTradeEntry(entry) && inRange(entry)) {
      capitalBeforeFirstCustomTrade = runningCapital;
    }
    if (entry.isBalanceAdjustment) {
      const targetBalance = Number(entry.adjustmentTarget ?? entry.accountBalance);
      runningCapital = Number.isFinite(targetBalance) ? targetBalance : runningCapital + (Number(entry.pnl) || 0);
    } else if (entry.isDeposit) {
      runningCapital += Number(entry.pnl) || 0;
    } else if (entry.isPayout) {
      runningCapital -= Math.abs(Number(entry.pnl) || 0);
    } else if (isTradeEntry(entry)) {
      runningCapital += Number(entry.pnl) || 0;
    }
  });

  const allTimeDeposits = sumPrecise(entries.filter((e) => e.isDeposit).map((e) => Number(e.pnl) || 0));
  const allTimePayouts = sumPrecise(entries.filter((e) => e.isPayout).map((e) => Number(e.pnl) || 0));

  let customCapitalBase = capitalBeforeFirstCustomTrade !== null ? capitalBeforeFirstCustomTrade : allTimeDeposits;
  if (customCapitalBase <= 0 && allTimeDeposits === 0 && allTimePayouts !== 0) {
    customCapitalBase = Math.abs(allTimePayouts);
  }
  if (customCapitalBase <= 0) {
    const firstTradeWithBalance = allEntriesSorted.find(
      (entry) => isTradeEntry(entry) && entry.accountBalance !== undefined && entry.accountBalance !== null && entry.accountBalance !== ''
    );
    if (firstTradeWithBalance) {
      const inferredCapital = (Number(firstTradeWithBalance.accountBalance) || 0) - (Number(firstTradeWithBalance.pnl) || 0);
      if (inferredCapital > 0) customCapitalBase = inferredCapital;
    }
  }

  return {
    pnl: customPnl,
    percentage: percentOf(customPnl, customCapitalBase, customTrades.length > 0),
    hasTrades: customTrades.length > 0,
    tradeCount: customTrades.length,
    startDate: customStartDate,
    endDate: customEndDate,
  };
}

/** Date shown on best/worst cards and the trade dialog (creation date first, as before). */
export function parseCreatedDate(entry) {
  if (entry.created) {
    const lastDashIndex = entry.created.lastIndexOf('-');
    return new Date(lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created);
  }
  if (entry.year && entry.month && entry.day) {
    return new Date(entry.year, entry.month - 1, entry.day);
  }
  return new Date();
}

/** Rules-tracking docs (`{ date, response }`) to compliance totals and current streak. */
export function computeRulesStats(rules) {
  const logged = (rules || [])
    .filter((rule) => rule.response === 'yes' || rule.response === 'no')
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const totalYes = logged.filter((r) => r.response === 'yes').length;
  const totalNo = logged.length - totalYes;
  let currentStreak = 0;
  for (const rule of logged) {
    if (rule.response !== 'yes') break;
    currentStreak += 1;
  }
  return { currentStreak, totalYes, totalNo };
}

/** Share of trades with a screenshot or written notes. */
export function computeReviewCoverage(tradeEntries) {
  const documented = tradeEntries.filter((trade) =>
    (Array.isArray(trade.screenshots) && trade.screenshots.length > 0)
    || String(trade.notes || trade.note || trade.description || '').trim()
  ).length;
  return {
    documented,
    total: tradeEntries.length,
    percent: tradeEntries.length > 0 ? (documented / tradeEntries.length) * 100 : 0,
  };
}

/** |avg win / avg loss|, or null when there are no losing trades. */
export function computePayoffRatio(avgWinningTrade, avgLosingTrade) {
  return Math.abs(avgLosingTrade || 0) > 0 ? Math.abs((avgWinningTrade || 0) / avgLosingTrade) : null;
}

export function getWeeklyFocus({ rulesTotal, rulesCompliance, recentForm, tradePace }) {
  if (rulesTotal > 0 && rulesCompliance < 80) {
    return 'Execution focus: improve rule adherence before adding more size or frequency.';
  }
  if (recentForm.recent.count >= 3 && recentForm.recent.totalPnl < 0) {
    return 'Review the notes and screenshots from the recent five before the next session.';
  }
  if (tradePace.status === 'Above normal') {
    return 'Trade pace is above your normal active day. Check that every entry still met your plan.';
  }
  return 'Execution is stable. Keep documenting the conditions behind each trade.';
}

/**
 * Y-axis domain for the equity curve. Never clamps at zero, so a negative
 * balance (or a dip below the starting balance) stays visible.
 */
export function computeEquityDomain(values) {
  const finite = (values || []).filter((v) => Number.isFinite(v));
  if (!finite.length) return [0, 1];
  const minY = Math.min(...finite);
  const maxY = Math.max(...finite);
  const span = maxY - minY;
  const padding = span > 0 ? span * 0.1 : Math.max(Math.abs(maxY) * 0.05, 1);
  return [minY - padding, maxY + padding];
}

/** 1, 2, 2.5 or 5 times a power of ten, at least `raw`. */
function niceStep(raw) {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / magnitude;
  const nice = [1, 2, 2.5, 5, 10].find((candidate) => fraction <= candidate + 1e-9) || 10;
  return nice * magnitude;
}

/**
 * Round-number y-axis ticks inside an equity domain (for example 0, 10k, 20k, 30k),
 * so the axis never shows odd values like $14.2k. Zero is always a tick when in range.
 */
export function computeEquityTicks([lo, hi], targetCount = 5) {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return [];
  let step = niceStep((hi - lo) / targetCount);
  const build = (size) => {
    const ticks = [];
    for (let value = Math.ceil(lo / size) * size; value <= hi + size * 1e-9; value += size) {
      ticks.push(Math.round(value / size) * size);
    }
    return ticks;
  };
  let ticks = build(step);
  // A coarse step can leave only one or two ticks; halve it until there are three.
  for (let guard = 0; ticks.length < 3 && guard < 4; guard += 1) {
    step = niceStep(step / 2.5);
    ticks = build(step);
  }
  return ticks.map((value) => (Object.is(value, -0) ? 0 : value));
}

/** Last `count` trades, oldest first, in the same order computeRecentForm uses. */
export function getRecentTrades(tradeEntries, count = 5) {
  const dateMs = (trade) => getInsightDate(trade)?.getTime() || 0;
  return [...tradeEntries].sort((a, b) => dateMs(a) - dateMs(b)).slice(-count);
}

/** Range chips shown on the equity curve card. Values match the page's Time Period options. */
export const EQUITY_RANGE_CHIPS = [
  { value: '1month', label: '1M', title: 'Last 30 days' },
  { value: '3months', label: '3M', title: 'Last 90 days' },
  { value: 'ytd', label: 'YTD', title: 'Year to date' },
  { value: 'all', label: 'All', title: 'All time' },
];

/**
 * Which equity chip is active. The chart follows the page Time Period until the
 * user picks a chip (`override`); a custom page period has no matching chip.
 */
export function getActiveEquityRange(pagePeriod, override = null) {
  if (override) return override;
  return EQUITY_RANGE_CHIPS.some((chip) => chip.value === pagePeriod) ? pagePeriod : null;
}

/**
 * Display-only: drop the zero-balance points before an account was first funded,
 * so the curve starts at the first deposit or balance adjustment instead of
 * rising from $0. Accounts that traded before funding (or never deposited) keep
 * their $0 baseline. Never trims below two points.
 */
export function trimUnfundedLead(points) {
  if (!Array.isArray(points)) return [];
  const fundedIndex = points.findIndex((point) => point?.type === 'Deposit' || point?.type === 'Balance Adjustment');
  if (fundedIndex <= 0) return points;
  const unfunded = points.slice(0, fundedIndex).every((point) => Number(point?.y) === 0);
  if (!unfunded) return points;
  const trimmed = points.slice(fundedIndex);
  return trimmed.length >= 2 ? trimmed : points;
}

/** Calendar destinations for the Summary stat tiles (MonthPage takes a 1-based month). */
export function getSummaryTileLinks(now = new Date()) {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return {
    today: `/day/${year}/${month}/${now.getDate()}`,
    week: `/month/${year}/${month}`,
    month: `/month/${year}/${month}`,
  };
}

const SESSION_DISPLAY_NAMES = {
  'pre-market': 'Pre-market',
  'NY-AM': 'NY AM',
  'NY-Lch': 'NY Lunch',
  'NY-PM': 'NY PM',
};

/**
 * Best weekday and session by average P&L per trade (expectancy), same rule and
 * minimum sample as the Analytics page's "Best weekday" / "Best session" tiles.
 * Only groups that actually made money are returned; otherwise null.
 */
export function getBestDayAndSession(tradeEntries, beThreshold = DEFAULT_BE_THRESHOLD, minimumSample = 3) {
  const trades = Array.isArray(tradeEntries) ? tradeEntries : [];
  const profitable = (group) => (group && group.expectancy > 0 ? group : null);
  const day = profitable(findBestGroup(trades, getDayOfWeek, minimumSample, 'expectancy', beThreshold));
  const session = profitable(findBestGroup(trades, (trade) => trade.tradeSession, minimumSample, 'expectancy', beThreshold));
  return {
    day,
    session: session ? { ...session, displayLabel: SESSION_DISPLAY_NAMES[session.label] || session.label } : null,
  };
}

/** Current trade streak as a compact badge ("3W" / "2L"), or null when there is none. */
export function getStreakBadge(streaks) {
  const wins = Number(streaks?.greenStreak) || 0;
  const losses = Number(streaks?.lossStreak) || 0;
  if (wins > 0) return { label: `${wins}W`, tone: 'win', description: `${wins}-trade winning streak` };
  if (losses > 0) return { label: `${losses}L`, tone: 'loss', description: `${losses}-trade losing streak` };
  return null;
}
